/**
 * Animates a token amount from zero to its final value when it scrolls into
 * view. The server renders the final formatted value (no hydration mismatch,
 * no-JS stays correct); the client rewinds to zero only when the element is
 * actually observed and motion is allowed.
 *
 * Amounts stay bigint end-to-end — the eased progress is applied as an
 * integer scale (basis points), never as a float on the raw value.
 */
import { useEffect, useRef, useState } from "react";

import { TOKEN_DECIMALS } from "@/lib/config";
import { formatDisplay } from "@/lib/format";

const SCALE = 10_000n;

export function CountUp({
  value,
  maxFractionDigits = 0,
  className,
  duration = 1400,
}: {
  value: bigint;
  maxFractionDigits?: number;
  className?: string;
  duration?: number;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [display, setDisplay] = useState(() =>
    formatDisplay(value, TOKEN_DECIMALS, maxFractionDigits),
  );

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!("IntersectionObserver" in window)) return;

    let frame = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();

        setDisplay(formatDisplay(0n, TOKEN_DECIMALS, maxFractionDigits));
        const start = performance.now();
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
          const scaled = (value * BigInt(Math.round(eased * Number(SCALE)))) / SCALE;
          setDisplay(formatDisplay(scaled, TOKEN_DECIMALS, maxFractionDigits));
          if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value, maxFractionDigits, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
